import React from 'react';
import { Paper, Stack, Typography, Chip, Button, FormControlLabel, Radio, Checkbox } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { HoldList } from 'api-client/models';
import { formatDateLong } from '@/utils/dateUtils';
import { STATUS_LABELS } from '../../../utils/constants';
import { getPositionBadgeStyles, getPositionText, getStatusColor } from '../../../utils/formatters';

interface MobileHoldCardProps {
  hold: HoldList;
  details?: { selected: boolean; disabled?: boolean; onSelect: () => void };
  batch?: { selected: boolean; disabled?: boolean; onSelect: () => void };
  onRespond?: (hold: HoldList) => void;
  children?: React.ReactNode;
}

/** Shared phone booking summary; detail and batch selections have separate labeled targets. */
export const MobileHoldCard: React.FC<MobileHoldCardProps> = ({ hold, details, batch, onRespond, children }) => {
  const navigate = useNavigate();
  return (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0, overflowWrap: 'anywhere' }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          {batch && (
            <Checkbox
              checked={batch.selected}
              disabled={batch.disabled}
              onChange={batch.onSelect}
              slotProps={{ input: { 'aria-label': `Select hold ${hold.id}` } }}
              sx={{ flexShrink: 0 }}
            />
          )}
          <Typography variant="subtitle2">{formatDateLong(hold.holdDate)}</Typography>
        </Stack>
        <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Chip
            label={getPositionText(hold.holdPosition || 0)}
            size="small"
            sx={getPositionBadgeStyles(hold.holdPosition || 0)}
          />
          <Chip label={STATUS_LABELS[hold.status] ?? hold.status} color={getStatusColor(hold.status)} size="small" />
        </Stack>
        <Typography variant="body2">
          {hold.stageName} · {hold.stageVenueName || 'N/A'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Promoter: {hold.promoterTeamName}
        </Typography>
        {hold.status === 'standby' && (
          <Typography variant="body2" color="text.secondary">
            This date is confirmed. Standby holds cannot be confirmed or challenged.
          </Typography>
        )}
        {hold.status === 'challenged' && hold.challengeResponseDeadline && (
          <Typography variant="body2" color="statusText.inFlight">
            Challenged by {hold.challengerTeamName || 'Unknown'} · Respond by{' '}
            {new Date(hold.challengeResponseDeadline).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </Typography>
        )}
        {hold.status === 'pending' && hold.hasInitiatedChallenge && (
          <Typography variant="body2" color="statusText.settled">
            Challenge Out · Awaiting response
          </Typography>
        )}
        {children}
        {details && (
          <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1 }}>
            {details && (
              <FormControlLabel
                sx={{ m: 0 }}
                label="Date details"
                control={
                  <Radio
                    name="show-date-details"
                    checked={details.selected}
                    disabled={details.disabled}
                    onChange={details.onSelect}
                    slotProps={{ input: { 'aria-label': `View details for ${hold.id}` } }}
                  />
                }
              />
            )}
          </Stack>
        )}
        <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate(`/bookings/${hold.id}`)}>
            Open hold
          </Button>
          {hold.status === 'challenged' && hold.challengeResponseDeadline && onRespond && (
            <Button variant="contained" color="warning" onClick={() => onRespond(hold)}>
              Respond
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};
